import {
    ActivityParentEntityId,
    ActivityRecordType,
    formatDateNoTime,
    UnconvertedInboundLeadEmailOutcome,
} from "@sp-crm/core";
import { CrmTable } from "components/table/crm-table";
import { CrmTableColumn } from "components/table/types";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import React from "react";
import { Link } from "react-router-dom";
import { hrefForActivityRecord } from "util/activity-record-href";

const NO_VALUE = "—";

interface StatTileProps {
    label: string;
    value: string;
}

const StatTile: React.FC<StatTileProps> = props => {
    const { label, value } = props;

    return (
        <div className="rounded-lg px-4 py-3 ring-1 ring-gray-200">
            <dt className="text-sm font-medium text-gray-500">{label}</dt>
            <dd className="mt-1 whitespace-nowrap text-2xl font-semibold text-gray-900">
                {value}
            </dd>
        </div>
    );
};

interface LeadSourceStatsProps {
    emailsReceived: number;
    leadsCreated: number;
    lastEmailAt: string | null;
}

export const LeadSourceStats: React.FC<LeadSourceStatsProps> = props => {
    const { emailsReceived, leadsCreated, lastEmailAt } = props;

    return (
        <div className="space-y-2">
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <StatTile
                    label="Emails received"
                    value={emailsReceived.toLocaleString()}
                />
                <StatTile label="Clients created" value={leadsCreated.toLocaleString()} />
                <StatTile
                    label="Last email"
                    value={formatDateNoTime(lastEmailAt, NO_VALUE)}
                />
            </dl>
            {emailsReceived === 0 ? (
                <InlineBanner type="info">
                    No email has arrived yet. Forward one to the address above to see it
                    work.
                </InlineBanner>
            ) : null}
        </div>
    );
};

export interface LeadSourceCreatedRecord {
    entityId: ActivityParentEntityId;
    entityType: ActivityRecordType;
    name: string;
    receivedAt: string;
}

const RECENT_CLIENT_COLUMNS: CrmTableColumn<LeadSourceCreatedRecord>[] = [
    {
        key: "name",
        header: "Client",
        renderCell: ({ record }) =>
            record ? (
                <Link
                    to={hrefForActivityRecord(record.entityType, record.entityId)}
                    className={defaultLinkStyle}>
                    {record.name}
                </Link>
            ) : (
                <span />
            ),
    },
    {
        key: "receivedAt",
        header: "Created",
        renderCell: ({ record }) => (
            <span>{record ? formatDateNoTime(record.receivedAt) : ""}</span>
        ),
    },
];

interface LeadSourceRecentClientsProps {
    recentlyCreated: LeadSourceCreatedRecord[];
}

export const LeadSourceRecentClients: React.FC<LeadSourceRecentClientsProps> = props => {
    const { recentlyCreated } = props;

    if (recentlyCreated.length === 0) {
        return null;
    }

    return (
        <div className="space-y-2">
            <p className="text-sm font-medium text-gray-500">Recent clients created</p>
            <CrmTable data={recentlyCreated} columns={RECENT_CLIENT_COLUMNS} />
        </div>
    );
};

export interface LeadSourceUnconvertedEmail {
    subject?: string;
    fromAddress?: string;
    outcome: UnconvertedInboundLeadEmailOutcome;
    receivedAt: string;
}

const NO_SUBJECT = "(no subject)";

const outcomeLabel = (outcome: UnconvertedInboundLeadEmailOutcome): string => {
    switch (outcome) {
        case "noData":
            return "No lead details found";
        case "failed":
            return "Could not be read";
        default: {
            const exhaustiveCheck: never = outcome;
            throw new Error(`Unhandled outcome: ${exhaustiveCheck}`);
        }
    }
};

interface LeadSourceUnconvertedEmailsProps {
    recentUnconverted: LeadSourceUnconvertedEmail[];
}

export const LeadSourceUnconvertedEmails: React.FC<
    LeadSourceUnconvertedEmailsProps
> = props => {
    const { recentUnconverted } = props;

    if (recentUnconverted.length === 0) {
        return null;
    }

    return (
        <div className="space-y-2">
            <p className="text-sm font-medium text-gray-500">
                Recent emails with no client
            </p>
            <ul className="divide-y divide-gray-200 rounded-lg ring-1 ring-gray-200">
                {recentUnconverted.map(email => (
                    <li
                        key={`${email.receivedAt}-${email.subject ?? ""}`}
                        className="flex items-baseline gap-3 px-3 py-2">
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm text-gray-900">
                                {email.subject || NO_SUBJECT}
                            </p>
                            <p className="truncate text-xs text-gray-500">
                                {email.fromAddress || NO_VALUE}
                            </p>
                        </div>
                        <div className="shrink-0 text-right">
                            <p className="text-xs font-medium text-gray-700">
                                {outcomeLabel(email.outcome)}
                            </p>
                            <p className="text-xs text-gray-500">
                                {formatDateNoTime(email.receivedAt)}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

interface SummaryFieldProps {
    label: string;
    value: string;
}

const SummaryField: React.FC<SummaryFieldProps> = props => {
    const { label, value } = props;

    return (
        <div className="min-w-0">
            <dt className="text-sm font-medium text-gray-500">{label}</dt>
            <dd className="mt-1 break-words text-sm text-gray-900">{value}</dd>
        </div>
    );
};

interface LeadSourceSummaryProps {
    attribution: string;
    assignee: string;
    notify: string;
}

export const LeadSourceSummary: React.FC<LeadSourceSummaryProps> = props => {
    const { attribution, assignee, notify } = props;

    return (
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <SummaryField label="Attributed to" value={attribution} />
            <SummaryField label="New clients assigned to" value={assignee} />
            <SummaryField label="Notified by email" value={notify} />
        </dl>
    );
};
